import { useEffect, useState } from "react";
import { get as commsGet } from "../../service/comms/commsApi";
import { listRooms } from "../../service/comms/roomsService";

const STORAGE_KEY = "company_id";

/**
 * Read the cached company id.
 *
 * Historically several comms screens read this key directly and assumed
 * something else had written it — nothing ever did, so they silently sent
 * `company_id: null` to ms-agents and got empty results back. This module is
 * now the only reader/writer.
 *
 * @returns {string|null}
 */
const readCachedCompanyId = () => {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
};

const cacheCompanyId = (value) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* private mode / quota — the in-memory value still works this session */
  }
};

/**
 * Resolve the caller's company id.
 *
 * There is no dedicated endpoint for this and it isn't part of the employee
 * profile, but every room record carries `company_id` — which is how
 * `OversightRoom` already derives it. We fetch one room, cache the result, and
 * reuse it for the rest of the session.
 *
 * @returns {{ companyId: string|null, loading: boolean, error: boolean }}
 */
export function useCompanyId() {
  const [companyId, setCompanyId] = useState(readCachedCompanyId);
  const [loading, setLoading] = useState(() => readCachedCompanyId() === null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (companyId !== null) return undefined;

    let active = true;
    setLoading(true);
    setError(false);

    listRooms(commsGet, { limit: 1 })
      .then((response) => {
        if (!active) return;
        const rooms = Array.isArray(response) ? response : response?.rooms || [];
        const found = rooms.find((room) => room?.company_id != null)?.company_id;

        if (found != null) {
          cacheCompanyId(found);
          setCompanyId(found);
        } else {
          // No rooms yet — the backend may still scope by the bearer token, so
          // this isn't necessarily fatal.
          setError(true);
        }
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to resolve company id:", err);
        setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [companyId]);

  return { companyId, loading, error };
}

export default useCompanyId;
